import React from 'react';

import Spinner from '@atlaskit/spinner';

import { SkeletonItem } from '@qality/common-components/src/atoms/SkeletonItem';
import { TestType, TestTypeAppearance } from '@qality/common-model';
import { StorageOfUniqueServices } from '@qality/common-service';
import { DEFAULT_TEST_TYPE } from '@qality/common-utils/src/TestType.const';

import { TestTypePopupSelect } from '../../select/TestTypePopupSelect';
import { TestTypeComponent } from '../TestTypeComponent';
import {
  IssueTestTypeStyled,
  SkeletonItemStyled,
  SpinnerStyled,
} from './IssueTestType.styled';

export type TestTypeLoading = 'spinner' | 'skeleton';

type Props = {
  testType: TestType;
  isLoading?: boolean;
  isReadOnly?: boolean;
  appearance?: TestTypeAppearance;
  loadingType?: TestTypeLoading;
  customStyle?: React.CSSProperties;
  isIconOnly?: boolean;
};

export default function IssueTestType({
  testType,
  isLoading = false,
  isReadOnly = true,
  appearance = 'subtle',
  loadingType = 'skeleton',
  customStyle,
  isIconOnly = true,
}: Props): JSX.Element {
  if (!StorageOfUniqueServices.get('LicenseService').isAdvanced) {
    return (
      <IssueTestTypeStyled style={customStyle}>
        <TestTypeComponent
          isIconOnly={isIconOnly}
          testType={DEFAULT_TEST_TYPE}
          appearance={appearance}
          showTooltip
        />
      </IssueTestTypeStyled>
    );
  }

  const getLoading = () => {
    if (loadingType === 'skeleton') {
      return (
        <SkeletonItemStyled>
          <SkeletonItem testId='issue-test-type-loading' width='24px' />
        </SkeletonItemStyled>
      );
    } else {
      return (
        <SpinnerStyled>
          <Spinner size='small' />
        </SpinnerStyled>
      );
    }
  };

  const getContent = () => {
    if (isLoading) {
      return getLoading();
    }

    if (isReadOnly) {
      return (
        <TestTypeComponent
          isIconOnly={isIconOnly}
          testType={testType}
          appearance={appearance}
          showTooltip
        />
      );
    }

    return <TestTypePopupSelect />;
  };

  return (
    <IssueTestTypeStyled style={customStyle}>
      {getContent()}
    </IssueTestTypeStyled>
  );
}
